/**************************************************************************************************************
 * Shared receipt formatting utilities used by both receiptCard.js (CSMCommonWeb display)
 * and PosPrintReceipt.js (ASBWorks POS printing). Used primarily to format receipts that
 * are being shown to the user like on the receipt card, and then same formatting necessary
 * to print the said receipts to the POS printers.
 * 
 * Pure ES6 — no Angular dependencies.  
 **************************************************************************************************************/


/**
 * Accounts are a unique set of receipt items that require special formatting on the receipt, as they
 * have a very different structure than our product items. This function takes in an account item and
 * returns the correct account name to display on the receipt based on the type of account item it is.
 * 
 * @param {*} item - the account item object containing type, name, and memo
 * @param {*} BooksDBConstants - houses the constants necessary to determine the correct labels and types
 * @returns {string} - the account name to display on the receipt
 */
export function getAccountName(item, BooksDBConstants) {
  if (item.type === BooksDBConstants.LIT_RECEIVABLE_CREDIT) return item.memo;
  if (item.type === BooksDBConstants.LIT_RECEIVABLE_DEBIT) return item.memo;
  if (item.type === BooksDBConstants.LIT_SERVICE_FEE_CREDIT) return 'Transaction Fee';
  if (item.type === BooksDBConstants.LIT_SERVICE_FEE_DEBIT) return 'Transaction Fee';
  if (!item.name) return item.memo;
  return item.name || '';
}


/**
 * Returns the payment amount, negating it if the payment type is returning money to the purchaser.
 * 
 * LIT_MONEY_PAID            -> Money returned to purchaser in cash.
 * LIT_STUDENT_FUNDS_CREDIT  -> Money returned by crediting student funds.
 * LIT_RECEIVABLE_CREDIT     -> Money returned by crediting A/R account.
 * LIT_JOURNAL_CREDIT        -> Money returned by writing a check. Only negated on return receipts.
 * 
 * 
 * @param {*} paymentItem - the payment item object containing type and amount
 * @param {*} BooksDBConstants - houses the constants necessary to determine the correct labels and types
 * @param {*} isReturnReceipt - boolean indicating if the receipt is a return receipt
 * @returns {number} - the calculated payment amount
 */
function getPaymentAmount(paymentItem, BooksDBConstants, isReturnReceipt = false) {
  switch (paymentItem.type) {
    case BooksDBConstants.LIT_MONEY_PAID:
    case BooksDBConstants.LIT_STUDENT_FUNDS_CREDIT:
    case BooksDBConstants.LIT_RECEIVABLE_CREDIT:
      return -paymentItem.amount;
    case BooksDBConstants.LIT_JOURNAL_CREDIT:
      return isReturnReceipt ? -paymentItem.amount : paymentItem.amount || 0;
    default:
      return paymentItem.amount || 0;
  }
}


/**
 * Calculates and returns the receipt dues: balanceDue, changeDue, and balanceReduced.
 * 
 * A receipt can have a balance due, change due, or balance reduced. 
 * It can not have more than 1 of these, and it will not have any of these if the 
 * amount paid is equal to the amount of the receipt.
 * 
 * @param {*} receipt - the receipt object containing payment items and amounts
 * @param {*} BooksDBConstants - houses the constants necessary to determine the correct labels and types
 * @returns {Object} - an object containing balanceDue, changeDue, and balanceReduced
 */
export function getReceiptDues(receipt, BooksDBConstants) {
  const isReturnReceipt = receipt.type === BooksDBConstants.LET_RETURN_RECEIPT;

  /*-----------------------------------------------------*/
  /* Calculate the total payments on the receipt. If the */
  /* payment is money paid, make it a negative amount.   */
  /*-----------------------------------------------------*/
  const totalPayments = Number(
    receipt.paymentItems.reduce(
      (total, paymentItem) =>
        total + getPaymentAmount(paymentItem, BooksDBConstants, isReturnReceipt),
      0
    )
  ).toFixed(2);

  /*------------------------------------------------------*/
  /* Difference is always displayed as a positive amount. */
  /*------------------------------------------------------*/
  const difference = Math.abs(receipt.amount - totalPayments);

  const balanceDue =
    difference &&
    receipt.amount > totalPayments &&
    receipt.type === BooksDBConstants.LET_RECEIPT
      ? difference
      : 0;
  const changeDue =
    receipt.amount < totalPayments &&
    receipt.type === BooksDBConstants.LET_RECEIPT
      ? difference
      : 0;
  const balanceReduced =
    receipt.amount < totalPayments &&
    receipt.type === BooksDBConstants.LET_RETURN_RECEIPT
      ? difference
      : 0;

  return { balanceDue, changeDue, balanceReduced };
}
